import React, { useEffect, useState, useCallback } from 'react';
import { inject, observer } from 'mobx-react';
import compose from 'helpers/compose';
import { useTranslation } from 'react-i18next';
import { useParams, useHistory } from 'react-router';
import * as routes from 'routes';

import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import NotificationImportantIcon from '@material-ui/icons/NotificationImportant';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import RefreshIcon from '@material-ui/icons/Refresh';
import ArrowBackIos from '@material-ui/icons/ArrowBackIos';

import Tooltip from 'components/Tooltip/Tooltip';
import Notifications from './components/Notifications';
import ContentSections from './components/ContentSections';
import ClientHeader from './components/ClientHeader';
import DepositInstructions from './Deposit/DepositInstructions';
import ClientViewDialog from './ClientView/ClientViewDialog';
import SideBar from './Sidebar/Sidebar';
import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog';
import ConfirmEditDialog from './components/ConfirmEditDialog';
import AdvisorInfo from 'components/AdvisorInfo/AdvisorInfo';
import Message, { MessageVariant } from 'components/Message/Message';

import useStyles from './styles';
import { AccountStatus, Client } from 'enums/index';
import { API_LOG_LOAD_ACCOUNT, API_LOG_LOAD_ACCOUNT_USER } from 'api/endpoints';
import { DetailPage, urls, Flows } from './enums';
import { Permission } from 'helpers/permissions';
import { Params } from '../Edit/accountEditRoutes';
import { AccountDetailStore } from 'stores/account/accountDetailStore';
import { RootStore } from 'stores/rootStore';
import { overdue } from 'stores/accountsStore';

interface Props {
  rootStore: RootStore;
  signatureAccess?: boolean;
}

const AccountDetail: React.FC<Props> = ({ rootStore, signatureAccess }) => {
  const history = useHistory();
  const { t } = useTranslation();
  const { userStore } = rootStore.stores;
  const advisorData = userStore.advisorData();

  const accountIdParam = useParams<Params>().accountId;

  const userMode = userStore.checkPermissions(Permission.APP_ENDUSER);
  const signOnly =
    userStore.activeAccount?.status === AccountStatus.TO_BE_SIGNED ||
    userStore.loggedUser?.client === Client.CLIENT_PORTAL_SIGN;

  const vsParam = userStore.activeAccountVs();
  const accountId = userMode ? userStore.activeAccount?.accountId || '' : accountIdParam;

  const [tab, setTab] = useState<DetailPage | undefined>(undefined); // default tab
  const [startFlow, setStartFlow] = useState<Flows | undefined>(undefined);

  const [depositsModalOpen, setDepositsModalOpen] = useState(false);
  const [clientViewModalOpen, setClientViewModalOpen] = useState(false);
  const [title, setTitle] = useState<string | undefined>(undefined);
  const [infoModalOpen, setInfoModalOpen] = useState(false);
  const [winTitle, setWinTitle] = useState('');

  const [store] = useState(new AccountDetailStore(rootStore, accountId, setTab, setTitle, userMode, signOnly));
  const classes = useStyles({ userMode, editMode: store.isEditMode });

  const changeTab = useCallback(
    (value?: DetailPage) => {
      const tabUrl = urls.find((url) => url.id === value);
      if (store.flow && store.flow.steps.some((s) => s.id === value)) {
        const title = t(`accountDetail_edit_${store.flow.id}`);
        setTitle(title);
        setWinTitle(title);
        window?.history?.replaceState(null, '', store.flow.url(accountId));
      } else if (
        tabUrl &&
        userStore.checkSignature(tabUrl.signature) &&
        userStore.checkPermissions(Permission.PAGE_DETAIL, value)
      ) {
        const windowTitle = tabUrl.title ? t(tabUrl.title) : tabUrl.windowTitle ? t(tabUrl.windowTitle) : '';
        setStartFlow(tabUrl.startFlow);
        store.setFlowStartPage(tabUrl.startFlowPage);

        window?.history?.replaceState(
          null,
          '',
          userMode ? tabUrl.clientUrl(vsParam || accountId) : tabUrl.advisorUrl(accountId),
        );
        setTitle(tabUrl.title);
        setWinTitle(windowTitle);
      }
    },
    [t, userMode, vsParam, accountId, userStore, store], //store
  );

  useEffect(() => {
    rootStore.stores.uiStore.setWindowTitle(
      signOnly
        ? t('pageTitle_signOnly')
        : `${t('pageTitle_accountDetail', {
            accountName: store.data?.detail?.title || userStore.activeAccount?.title,
          })} - ${winTitle}`,
    );
  }, [signOnly, winTitle, rootStore.stores.uiStore, t, store.data?.detail?.title, userStore.activeAccount?.title]);

  useEffect(() => {
    if (store.dataProcessed && (userMode || !store.isEditMode)) {
      const { pathname } = history.location;
      const tabUrl = urls.find((tab) => {
        return userMode ? tab.clientUrl(vsParam || accountId) === pathname : tab.advisorUrl(accountId) === pathname;
      });
      if (
        tabUrl &&
        userStore.checkSignature(tabUrl.signature) &&
        userStore.checkPermissions(Permission.PAGE_DETAIL, tabUrl.id)
      ) {
        //console.log('url');
        store.setPage(tabUrl.id);
      } else {
        store.setPage(
          userStore.activeAccount?.status === AccountStatus.TO_BE_SIGNED ||
            userStore.activeAccount?.pendingSignature ||
            userStore.activeAccount?.pendingRequestSignature
            ? // TODO: check
              //userStore.activeAccount?.pendingRequest
              DetailPage.SIGNATURE
            : userStore.checkPermissions(Permission.ACCESS_USER_FULL)
            ? DetailPage.DASHBOARD
            : DetailPage.REPORTS,
        );
      }
    } else if (signOnly) {
      store.setPage(DetailPage.SIGNATURE);
    }
  }, [store, store.dataProcessed, userStore, accountId, userMode, history.location, changeTab, vsParam, signOnly]);

  useEffect(() => {
    changeTab(store.currentPage);
  }, [changeTab, store.currentPage]);

  useEffect(() => {
    rootStore.stores.uiStore.setDetail(true);
    if (accountId) {
      // FIXME: DRF - check ifs
      if (signOnly) {
        store.setPage(DetailPage.SIGNATURE);
      } else {
        store.setAccountId(accountId);
        rootStore.stores.uiStore.setCurrentAccountId(accountId, userMode);
        rootStore.stores.apiStore.post(
          userMode ? API_LOG_LOAD_ACCOUNT_USER(accountId) : API_LOG_LOAD_ACCOUNT(accountId),
        );
        store.fetchData();
      }
    } else if (signOnly && signatureAccess) {
      store.setPage(DetailPage.SIGNATURE);
    }

    return () => {
      rootStore.stores.uiStore.unsetCurrentAccountId();
      rootStore.stores.uiStore.setDetail(false);
    };
  }, [store, rootStore.stores.apiStore, rootStore.stores.uiStore, accountId, userMode, signOnly, signatureAccess]);

  useEffect(() => {
    rootStore.stores.uiStore.setUserMenuData({
      store,
      openInfoModal: setInfoModalOpen,
      openDepositModal: setDepositsModalOpen,
      openClientViewModal: setClientViewModalOpen,
    });
  }, [
    rootStore.stores.uiStore,
    store,
    store.currentPage,
    store.status,
    store.editMode,
    store.isEditDisabled,
    //store.section,
    store.flow,
    store.data.detail,
    store.processing,
  ]);

  // TODO? move to store? general account notification/state system?
  const notifications: { [key: string]: string } = {};
  const iqOverdue = !!(
    store?.data?.detail && overdue.includes(store.data.detail.investmentQuestionnaireValidityDate.severity)
  );
  if (iqOverdue) {
    notifications[DetailPage.ID] = t('accounts_questionnaireOverdue');
  }

  if (!signOnly && userMode && rootStore.stores.userStore.activeAccount?.accountId !== store.accountId) {
    return null;
  }

  if (!signOnly && (!store.data.summary || store.editMode === undefined)) {
    // || store.section === undefined
    return null;
  }

  return (
    <Grid container className={classes.root} spacing={0}>
      {!userMode && (
        <>
          <Grid container item xs={12} alignItems="stretch" spacing={0} className="DETAIL_HEADER">
            <span
              className={classes.accounts}
              onClick={() =>
                history.push(
                  store.locked
                    ? routes.ACCOUNTS_INACTIVE
                    : store.status === AccountStatus.AWAITING_APPROVAL
                    ? routes.ACCOUNTS_DRAFTS
                    : routes.ACCOUNTS_ACTIVE,
                )
              }
            >
              <ArrowBackIos /> {t('backOffice_accounts_title')}
            </span>

            <ClientHeader
              store={store}
              rootStore={rootStore}
              tabChange={setTab}
              openClientView={setClientViewModalOpen}
              accountId={accountId}
              iqOverdue={iqOverdue}
            />
          </Grid>
        </>
      )}
      <Notifications store={store} iqOverdue={iqOverdue} userMode={userMode} />

      {(!store.processing || userMode) && (
        <>
          <Grid item xs={12} className="DETAIL_CONTENT">
            <div
              className={`${classes.actionsWrapper} ${
                store.currentPage !== DetailPage.PLAN ? 'MuiContainer-root MuiContainer-maxWidthMd' : ''
              }`}
            >
              <Grid item xs={1} sm={12} className="DETAIL_MENU">
                <SideBar rootStore={rootStore} insite />
              </Grid>

              {title && (
                <Grid
                  item
                  xs={12}
                  // FIXME: DRF - check classes
                  className={`${classes.title} ${store.currentPage}-page ${store.isEditMode ? 'EDIT' : ''}`}
                >
                  <Typography variant="h2">
                    <span>{t(title)}</span>
                    {(store.currentPage === DetailPage.DASHBOARD || store.currentPage === DetailPage.PLAN) &&
                      !store.isEditMode && (
                        <Tooltip title={t('accountDetail_refreshData') || ''} placement="top">
                          <IconButton size="small" onClick={store.refreshData}>
                            <RefreshIcon fontSize="large" />
                          </IconButton>
                        </Tooltip>
                      )}
                  </Typography>
                </Grid>
              )}

              <Grid
                container
                item
                xs={'auto'}
                sm={12}
                spacing={0}
                className={`DETAIL_ACTIONS ${store.isEditMode ? 'EDIT' : ''}`}
              >
                {!userMode && (startFlow || store.isEditMode) && (
                  <Grid container alignItems="center">
                    <Grid item xs={12} className={classes.actionButtons}>
                      {!store.isEditMode && startFlow ? (
                        <Button
                          variant="contained"
                          color={'primary'}
                          disabled={
                              store.currentPage === DetailPage.PROFILE
                              ? store.isEdit03Disabled
                              : store.isEditDisabled || store.isEditTabDisabled(store.currentPage)
                          }
                          onClick={() => store.edit(startFlow)}
                          className={
                            store.currentPage && store.currentPage in notifications ? classes.buttonWithNote : undefined
                          }
                        >
                          {t(`accountDetail_edit_${store.currentPage}`)}
                          {store.currentPage && store.currentPage in notifications && (
                            <Tooltip title={notifications[store.currentPage]}>
                              <NotificationImportantIcon />
                            </Tooltip>
                          )}
                        </Button>
                      ) : (
                        !store.pendingSignature && (
                          <Message
                            id="accountDetail_cancel"
                            message={t('accountDetail_cancelEdits_info')}
                            variant={MessageVariant.ACTION}
                            permanent
                            action={store.cancel}
                            actionText={t('accountDetail_cancelEdits')}
                            responsive
                          />
                        )
                      )}
                    </Grid>
                  </Grid>
                )}
              </Grid>
            </div>

            <ContentSections
              accountId={accountId}
              store={store}
              tab={tab}
              setTab={setTab}
              userMode={userMode}
              setDepositsModalOpen={setDepositsModalOpen}
              refreshTimestamp={store.refreshTimestamp}
            />
          </Grid>
        </>
      )}

      <ConfirmEditDialog store={store} />

      <ConfirmDialog
        noHeader
        isOpen={store.confirmRequest}
        confirm={async () => {
          store.cancelRequest();
        }}
        close={store.hideConfirmRequest}
      >
        {t('accountDetail_confirm_cancelRequest')}
      </ConfirmDialog>

      <ConfirmDialog
        noHeader
        isOpen={store.infoDialog}
        confirm={async () => {
          store.hideInfoDialog();
        }}
      >
        {t(store.infoText ? store.infoText : '')}
      </ConfirmDialog>

      {advisorData && (
        <ConfirmDialog
          title={null}
          noButtons
          maxWidth="xs"
          isOpen={infoModalOpen}
          close={() => {
            setInfoModalOpen(false);
          }}
        >
          <AdvisorInfo advisorData={advisorData} />
        </ConfirmDialog>
      )}

      {depositsModalOpen && (
        <DepositInstructions
          openDeposit={setDepositsModalOpen}
          accountId={accountId}
          currency={store.currency}
          mode={store.mode}
          personalNumber={store.data.summary?.ownerData.personalNumber}
          accountTitle={store.data.detail?.title}
        />
      )}
      {clientViewModalOpen && (
        <ClientViewDialog openClientView={setClientViewModalOpen} accountDetailStore={store} rootStore={rootStore} />
      )}
    </Grid>
  );
};

export default compose(inject('rootStore'), observer)(AccountDetail);
